import { Icon } from '@iconify/react'
import { Modal } from 'antd'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatDate } from 'common/services/format'
import type { ESignLogEntry } from 'features/Procedure/state/procedure-api.type'
import styles from './e-sign-modal.module.scss'

const KEYPAD_LAYOUT = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['', '0', 'backspace'],
] as const

type Props = {
  isVisible: boolean
  onClose: () => void
  onSubmit: (pin: string) => Promise<boolean> | boolean
  description: string
  pinLength: number
  signHistory?: ESignLogEntry[]
  isLoading?: boolean
}

const getSignerName = (entry: ESignLogEntry): string => {
  return `${entry.fname || ''} ${entry.lname || ''}`.trim() || 'Unknown'
}

const getSignerInitials = (entry: ESignLogEntry): string => {
  const fullName = getSignerName(entry)

  return fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export const ESignModal = ({
  isVisible,
  onClose,
  onSubmit,
  description,
  pinLength,
  signHistory = [],
  isLoading = false,
}: Props) => {
  const [pin, setPin] = useState('')
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [isSubmittingPin, setIsSubmittingPin] = useState(false)
  const pinRef = useRef(pin)
  const isSubmittingPinRef = useRef(false)
  const activeKeyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isBusy = isLoading || isSubmittingPin

  const clearActiveKeyTimeout = useCallback(() => {
    if (!activeKeyTimeoutRef.current) return
    clearTimeout(activeKeyTimeoutRef.current)
    activeKeyTimeoutRef.current = null
  }, [])

  const resetPin = useCallback(() => {
    pinRef.current = ''
    setPin('')
    clearActiveKeyTimeout()
    setActiveKey(null)
  }, [clearActiveKeyTimeout])

  const setPinValue = useCallback((nextPin: string) => {
    pinRef.current = nextPin
    setPin(nextPin)
  }, [])

  const animateKeyPress = useCallback(
    (key: string) => {
      clearActiveKeyTimeout()
      setActiveKey(key)
      activeKeyTimeoutRef.current = setTimeout(() => {
        setActiveKey(null)
        activeKeyTimeoutRef.current = null
      }, 160)
    },
    [clearActiveKeyTimeout],
  )

  const submitPin = useCallback(
    async (pinValue: string) => {
      if (!isVisible || isLoading || isSubmittingPinRef.current || pinValue.length !== pinLength) {
        return false
      }

      isSubmittingPinRef.current = true
      setIsSubmittingPin(true)

      try {
        const didSubmit = await Promise.resolve(onSubmit(pinValue))
        if (!didSubmit) {
          resetPin()
        }
        return didSubmit
      } catch {
        resetPin()
        return false
      } finally {
        isSubmittingPinRef.current = false
        setIsSubmittingPin(false)
      }
    },
    [isLoading, isVisible, onSubmit, pinLength, resetPin],
  )

  const handleDigitPress = useCallback(
    (digit: string) => {
      if (!isVisible || isLoading || isSubmittingPinRef.current) return

      const currentPin = pinRef.current
      if (currentPin.length >= pinLength) return

      animateKeyPress(digit)
      const nextPin = `${currentPin}${digit}`.slice(0, pinLength)
      setPinValue(nextPin)

      if (nextPin.length === pinLength) {
        void submitPin(nextPin)
      }
    },
    [animateKeyPress, isLoading, isVisible, pinLength, setPinValue, submitPin],
  )

  const handleBackspace = useCallback(() => {
    if (!isVisible || isLoading || isSubmittingPinRef.current) return

    const currentPin = pinRef.current
    if (currentPin.length === 0) return

    animateKeyPress('backspace')
    setPinValue(currentPin.slice(0, -1))
  }, [animateKeyPress, isLoading, isVisible, setPinValue])

  const handleClose = useCallback(() => {
    if (isBusy) return
    resetPin()
    onClose()
  }, [isBusy, onClose, resetPin])

  const pinDisplayTokens = useMemo(() => {
    return Array.from({ length: pinLength }, (_, index) => pin[index] ?? '_')
  }, [pin, pinLength])

  const hasSignHistory = signHistory.length > 0

  useEffect(() => {
    pinRef.current = pin
  }, [pin])

  useEffect(() => {
    if (!isVisible) {
      resetPin()
    }
  }, [isVisible, resetPin])

  useEffect(() => {
    if (!isVisible) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault()
        handleDigitPress(event.key)
        return
      }

      if (event.key === 'Backspace') {
        event.preventDefault()
        handleBackspace()
        return
      }

      if (event.key === 'Enter' && pinRef.current.length === pinLength) {
        event.preventDefault()
        void submitPin(pinRef.current)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleBackspace, handleDigitPress, isVisible, pinLength, submitPin])

  useEffect(() => {
    return () => {
      clearActiveKeyTimeout()
    }
  }, [clearActiveKeyTimeout])

  return (
    <Modal
      closeIcon={null}
      classNames={{ content: styles.modalContent }}
      open={isVisible}
      onCancel={handleClose}
      keyboard={!isBusy}
      maskClosable={!isBusy}
      title={null}
      destroyOnHidden
      footer={null}
      width={560}>
      <div className={styles.content}>
        <p className={styles.description}>{description}</p>
        <div className={styles.keypadPanel}>
          <div
            aria-live='polite'
            aria-label={`PIN entry ${pin.length} of ${pinLength} digits`}
            className={styles.pinDisplay}>
            {pinDisplayTokens.map((token, index) => (
              <span
                key={`${token}-${index}`}
                className={classNames(styles.pinDisplayToken, {
                  [styles.pinDisplayTokenFilled]: pin[index],
                })}>
                {token}
              </span>
            ))}
          </div>
          <div className={styles.keypadGrid}>
            {KEYPAD_LAYOUT.flatMap((row, rowIndex) =>
              row.map((key, columnIndex) => {
                if (!key) {
                  return (
                    <span
                      key={`empty-${rowIndex}-${columnIndex}`}
                      aria-hidden='true'
                      className={styles.keySpacer}
                    />
                  )
                }

                const isBackspace = key === 'backspace'

                return (
                  <button
                    key={key}
                    type='button'
                    aria-label={isBackspace ? 'Delete last PIN digit' : `PIN digit ${key}`}
                    className={classNames(styles.keypadKey, {
                      [styles.keypadKeyDanger]: isBackspace,
                      [styles.keypadKeyPressed]: activeKey === key,
                    })}
                    disabled={isBusy}
                    onClick={() => {
                      if (isBackspace) {
                        handleBackspace()
                        return
                      }
                      handleDigitPress(key)
                    }}>
                    {isBackspace ? (
                      <Icon icon='material-symbols:backspace-outline-rounded' width={24} />
                    ) : (
                      key
                    )}
                  </button>
                )
              }),
            )}
          </div>
          <p className={styles.statusText}>{isBusy ? 'Signing...' : ' '}</p>
        </div>
        {hasSignHistory && (
          <section className={styles.historySection} aria-label='Previous e-sign history'>
            <div className={styles.historyHeader}>
              <h3 className={styles.historyTitle}>Previous signatures</h3>
              <span className={styles.historyCount}>{signHistory.length}</span>
            </div>
            <div className={styles.historyList}>
              {signHistory.map((entry, index) => {
                const signerName = getSignerName(entry)
                const formattedDate = formatDate(entry.datetime, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })

                return (
                  <article key={`${entry.datetime}-${index}`} className={styles.historyItem}>
                    <div className={styles.historyAvatar}>{getSignerInitials(entry)}</div>
                    <div className={styles.historyBody}>
                      <div className={styles.historyMeta}>
                        <div className={styles.historyNameRow}>
                          <span className={styles.historyName}>{signerName}</span>
                          {index === 0 && <span className={styles.historyBadge}>Latest</span>}
                        </div>
                        <span className={styles.historyDate}>{formattedDate}</span>
                      </div>
                      {entry.amendment && (
                        <p className={styles.historyAmendment}>
                          <span className={styles.historyAmendmentLabel}>Amendment:</span>{' '}
                          {entry.amendment}
                        </p>
                      )}
                    </div>
                  </article>
                )
              })}
            </div>
          </section>
        )}
      </div>
    </Modal>
  )
}
